import { apiFetch } from "@/lib/api";

export type User = {
  id: string;
  full_name: string;
  email: string | null;
  department?: string | null;
  created_at: string;
  updated_at: string;
  role: string;
  phone_number?: string | null;
  profile_photo?: string | null;
  active?: boolean;
};

export type UserListResponse = {
  items: User[];
  total: number;
};

export type CreateUserPayload = {
  fullName: string;
  email?: string | null;
  password: string;
  phoneNumber?: string;
  department?: string | null;
  role: string;
};

export type UpdateUserPayload = {
  password?: string;
  phoneNumber?: string | null;
  department?: string | null;
  role?: string;
};

export const fetchUsers = async (params: {
  page: number;
  size: number;
  search?: string;
  sortField?: string | null;
  sortOrder?: string | null;
}): Promise<UserListResponse> => {
  const query = new URLSearchParams();
  query.set("page", String(params.page));
  query.set("size", String(params.size));
  if (params.search) query.set("search", params.search);
  if (params.sortField) query.set("sortField", params.sortField);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
  const payload = await apiFetch<UserListResponse | null>(`/users?${query.toString()}`);
  return payload ?? { items: [], total: 0 };
};

export const listUsers = async (params?: { size?: number }): Promise<User[]> => {
  const size = params?.size ?? 2000;
  const payload = await fetchUsers({ page: 1, size });
  return payload.items || [];
};

export const createUser = async (payload: CreateUserPayload): Promise<User> => {
  return apiFetch<User>("/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const getUser = async (id: string): Promise<User> => {
  return apiFetch<User>(`/users/${id}`);
};

export const updateUser = async (id: string, payload: UpdateUserPayload): Promise<User> => {
  return apiFetch<User>(`/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

export const deleteUser = async (id: string): Promise<void> => {
  await apiFetch<void>(`/users/${id}`, { method: "DELETE" });
};

export const setUserActive = async (id: string, active: boolean): Promise<User> => {
  return apiFetch<User>(`/users/${id}/active`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
};

export const lookupUsersByRoles = async (roles: string[]): Promise<User[]> => {
  const query = new URLSearchParams();
  query.set("roles", roles.join(","));
  return apiFetch<User[]>(`/users/lookup?${query.toString()}`);
};
